import * as THREE from "three";
import { CAR_PRESETS, type CarUpgrade, type VehicleBlueprint } from "@neon-arena/shared";
import { SceneManager } from "../game/SceneManager.js";
import { VehicleController } from "../physics/VehicleController.js";
import { buildVehicleVisual, type VehicleVisual } from "../rendering/VehicleRenderer.js";
import { TestRunner, type CombatResult, type TestPhase } from "./TestRunner.js";
import { TestLabUI } from "../ui/TestLabUI.js";

const LANE_X = [-240, -120, 0, 120, 240];
const START_Z = -180;
const NO_OBSTACLES: never[] = [];
const TEST_ARENA_SIZE = 800;
const ARENA_WIDTH = 700;
const ARENA_DEPTH = 500;
const BAY_RADIUS = 50;

const DUMMY_DISTANCE = 80;
const DUMMY_HP = 100;
const DUMMY_HIT_RADIUS = 2.5;

const RESET_ON_BEGIN = new Set<TestPhase>(["speed", "corner_warmup", "drift_warmup", "boost", "combat"]);

interface VehicleLane {
  blueprint: VehicleBlueprint;
  controller: VehicleController;
  visual: VehicleVisual;
  labelSprite: THREE.Sprite;
  labelCtx: CanvasRenderingContext2D;
  labelTex: THREE.CanvasTexture;
  labelName: string;
  spawnX: number;
  spawnZ: number;
  reloadTimer: number;
  shotsFired: number;
  shotsHit: number;
}

interface Dummy {
  mesh: THREE.Mesh;
  edges: THREE.LineSegments;
  hpBar: THREE.Mesh;
  hp: number;
  x: number;
  z: number;
  destroyedAtMs: number | null;
}

interface Projectile {
  mesh: THREE.Mesh;
  x: number;
  z: number;
  vx: number;
  vz: number;
  ownerLane: number;
  damage: number;
  ttl: number;
}

export class TestLab {
  runner = new TestRunner();
  private ui = new TestLabUI();
  lanes: VehicleLane[] = [];
  dummies: Dummy[] = [];
  projectiles: Projectile[] = [];
  private animFrame = 0;
  private lastFrame = 0;
  private paused = false;
  private prevPhase: TestPhase = "idle";
  private combatStartTime = 0;
  private combatDone = false;
  private onExit: () => void;
  private keyHandler: (e: KeyboardEvent) => void;

  constructor(
    private scene: SceneManager,
    private blueprints: VehicleBlueprint[],
    private upgrades: CarUpgrade[],
    onExit: () => void,
  ) {
    this.onExit = onExit;
    this.keyHandler = (e: KeyboardEvent) => this.onKey(e);
    if (import.meta.env.DEV) {
      (window as unknown as { __testLab: TestLab }).__testLab = this;
    }
  }

  start(): void {
    // Clear any content from a previous run (shared scene)
    while (this.scene.scene.children.length > 0) {
      this.scene.scene.remove(this.scene.scene.children[0]);
    }
    this.scene.scene.fog = null;
    this.buildArena();
    this.spawnVehicles();
    this.runner.init(
      this.lanes.map((l) => l.blueprint),
      this.lanes.map((l) => l.controller),
    );
    this.ui.show();
    this.ui.setVehicleNames(this.lanes.map((l) => l.blueprint.name));
    window.addEventListener("keydown", this.keyHandler);
    this.lastFrame = performance.now();
    this.prevPhase = this.runner.phase;
    this.loop();
  }

  stop(): void {
    cancelAnimationFrame(this.animFrame);
    window.removeEventListener("keydown", this.keyHandler);
    this.ui.hide();
    while (this.scene.scene.children.length > 0) {
      this.scene.scene.remove(this.scene.scene.children[0]);
    }
    this.lanes = [];
    this.dummies = [];
    this.projectiles = [];
    this.runner.reset();
    this.onExit();
  }

  private loop = (): void => {
    const now = performance.now();
    let dt = (now - this.lastFrame) / 1000;
    if (dt > 0.1) dt = 0.1;
    this.lastFrame = now;

    if (!this.paused) {
      const inputs = this.runner.tick(dt);
      const phaseChanged = this.runner.phase !== this.prevPhase;

      if (phaseChanged) {
        if (RESET_ON_BEGIN.has(this.runner.phase)) {
          this.resetVehicles();
        }
        if (this.runner.phase === "combat") {
          this.beginCombat(now);
        }
        if (this.prevPhase === "combat" && this.runner.phase !== "combat") {
          this.clearCombat();
        }
      }

      // apply driving inputs
      for (let i = 0; i < this.lanes.length; i++) {
        const lane = this.lanes[i];
        lane.controller.applyInput(inputs[i], dt);
        const snap = lane.controller.snapshot();
        lane.visual.group.position.set(snap.x, 0, snap.z);
        lane.visual.group.rotation.y = snap.rotY;
        this.updateLabel(lane, snap.speed);
        lane.labelSprite.position.set(snap.x, 3.5, snap.z);
      }

      if (this.runner.phase === "combat") {
        this.tickCombat(dt, now);
      }

      this.ui.update(
        this.runner.getPhaseLabel(),
        this.runner.getPhaseProgress(),
        this.lanes.map((l) => ({ name: l.blueprint.name, speed: l.controller.snapshot().speed })),
      );

      if (this.runner.phase === "done" && this.prevPhase !== "done") {
        const results = this.runner.getResults();
        if (results) {
          this.ui.showResults(results);
          this.ui.showExportButton(results, () => {});
        }
      }

      if (this.runner.waitingForInput && phaseChanged) {
        this.ui.showPartialResults(this.runner.getPartialResults());
      }

      this.prevPhase = this.runner.phase;
    }

    this.updateCamera();
    this.scene.render();
    this.animFrame = requestAnimationFrame(this.loop);
  };

  // ──────────────────────────────────────────────────────────
  //  Combat simulation
  // ──────────────────────────────────────────────────────────

  private beginCombat(nowMs: number): void {
    this.spawnDummies();
    this.combatStartTime = nowMs;
    this.combatDone = false;
    for (const lane of this.lanes) {
      lane.reloadTimer = 0;
      lane.shotsFired = 0;
      lane.shotsHit = 0;
    }
  }

  private clearCombat(): void {
    for (const d of this.dummies) {
      this.scene.scene.remove(d.mesh);
      this.scene.scene.remove(d.edges);
      this.scene.scene.remove(d.hpBar);
    }
    for (const p of this.projectiles) {
      this.scene.scene.remove(p.mesh);
    }
    this.dummies = [];
    this.projectiles = [];
  }

  private spawnDummies(): void {
    for (let i = 0; i < this.lanes.length; i++) {
      const x = LANE_X[i];
      const z = START_Z + DUMMY_DISTANCE;
      const geo = new THREE.BoxGeometry(2.5, 2.6, 2.5);
      const mat = new THREE.MeshBasicMaterial({ color: 0x1e293b, transparent: true, opacity: 0.85 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, 1.3, z);
      this.scene.scene.add(mesh);

      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(geo),
        new THREE.LineBasicMaterial({ color: 0xf472b6 }),
      );
      edges.position.copy(mesh.position);
      this.scene.scene.add(edges);

      const hpGeo = new THREE.PlaneGeometry(2.6, 0.3);
      const hpMat = new THREE.MeshBasicMaterial({ color: 0xa3e635, side: THREE.DoubleSide });
      const hpBar = new THREE.Mesh(hpGeo, hpMat);
      hpBar.position.set(x, 3.6, z);
      hpBar.rotation.x = -0.4;
      this.scene.scene.add(hpBar);

      this.dummies.push({ mesh, edges, hpBar, hp: DUMMY_HP, x, z, destroyedAtMs: null });
    }
  }

  private tickCombat(dt: number, nowMs: number): void {
    if (this.combatDone) return;

    for (let i = 0; i < this.lanes.length; i++) {
      const lane = this.lanes[i];
      const dummy = this.dummies[i];
      if (!dummy || dummy.destroyedAtMs !== null) continue;
      lane.reloadTimer += dt * 1000;
      if (lane.reloadTimer >= lane.blueprint.weapon.reloadMs) {
        lane.reloadTimer = 0;
        lane.shotsFired++;
        this.spawnProjectile(i, lane);
      }
    }

    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.x += p.vx * dt;
      p.z += p.vz * dt;
      p.ttl -= dt;
      p.mesh.position.set(p.x, 1.0, p.z);

      const dummy = this.dummies[p.ownerLane];
      if (dummy && dummy.destroyedAtMs === null) {
        const dx = p.x - dummy.x;
        const dz = p.z - dummy.z;
        if (dx * dx + dz * dz < DUMMY_HIT_RADIUS * DUMMY_HIT_RADIUS) {
          dummy.hp -= p.damage;
          this.lanes[p.ownerLane].shotsHit++;
          this.updateDummyHp(dummy);
          this.scene.scene.remove(p.mesh);
          this.projectiles.splice(i, 1);
          if (dummy.hp <= 0) {
            dummy.destroyedAtMs = nowMs;
            this.destroyDummy(dummy);
          }
          continue;
        }
      }
      if (p.ttl <= 0) {
        this.scene.scene.remove(p.mesh);
        this.projectiles.splice(i, 1);
      }
    }

    if (this.dummies.every((d) => d.destroyedAtMs !== null)) {
      this.completeCombat(nowMs);
    }
  }

  private spawnProjectile(laneIdx: number, lane: VehicleLane): void {
    const snap = lane.controller.snapshot();
    const dummy = this.dummies[laneIdx];
    const dx = dummy.x - snap.x;
    const dz = dummy.z - snap.z;
    const dist = Math.sqrt(dx * dx + dz * dz) || 1;
    const speed = lane.blueprint.weapon.projectileSpeed;
    const vx = (dx / dist) * speed;
    const vz = (dz / dist) * speed;

    const geo = new THREE.SphereGeometry(0.4, 8, 8);
    const mat = new THREE.MeshBasicMaterial({ color: 0x22d3ee });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(snap.x, 1.0, snap.z);
    this.scene.scene.add(mesh);

    this.projectiles.push({
      mesh, x: snap.x, z: snap.z, vx, vz,
      ownerLane: laneIdx,
      damage: lane.blueprint.weapon.damage,
      ttl: 5,
    });
  }

  private updateDummyHp(dummy: Dummy): void {
    const pct = Math.max(0, dummy.hp / DUMMY_HP);
    dummy.hpBar.scale.x = pct;
    const mat = dummy.hpBar.material as THREE.MeshBasicMaterial;
    mat.color.setHex(pct > 0.5 ? 0xa3e635 : pct > 0.25 ? 0xfb923c : 0xf472b6);
  }

  private destroyDummy(dummy: Dummy): void {
    (dummy.mesh.material as THREE.MeshBasicMaterial).opacity = 0.15;
    (dummy.edges.material as THREE.LineBasicMaterial).color.setHex(0x4a5568);
    dummy.hpBar.visible = false;
  }

  private completeCombat(nowMs: number): void {
    if (this.combatDone) return;
    this.combatDone = true;
    const results: CombatResult[] = this.lanes.map((lane, i) => {
      const w = lane.blueprint.weapon;
      const dummy = this.dummies[i];
      const ttk = dummy && dummy.destroyedAtMs !== null
        ? (dummy.destroyedAtMs - this.combatStartTime) / 1000
        : null;
      return {
        id: lane.blueprint.id,
        name: lane.blueprint.name,
        damagePerShot: w.damage,
        reloadMs: w.reloadMs,
        projectileSpeed: w.projectileSpeed,
        shotsFired: lane.shotsFired,
        shotsHit: lane.shotsHit,
        timeToKillS: ttk,
        dps: w.damage / (w.reloadMs / 1000),
      };
    });
    this.runner.setCombatResults(results);
    this.runner.nextPhase();
  }

  // ──────────────────────────────────────────────────────────
  //  Camera — adaptive bounding-box framing
  // ──────────────────────────────────────────────────────────

  private updateCamera(): void {
    if (this.lanes.length === 0) {
      this.scene.camera.position.set(0, 80, START_Z - 80);
      this.scene.camera.lookAt(0, 0, 0);
      return;
    }
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const l of this.lanes) {
      const x = l.controller.x, z = l.controller.z;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
    }
    if (this.runner.phase === "combat") {
      for (const d of this.dummies) {
        if (d.x < minX) minX = d.x; if (d.x > maxX) maxX = d.x;
        if (d.z < minZ) minZ = d.z; if (d.z > maxZ) maxZ = d.z;
      }
    }
    const padX = 25, padZ = 30;
    minX -= padX; maxX += padX; minZ -= padZ; maxZ += padZ;
    const centerX = (minX + maxX) / 2;
    const centerZ = (minZ + maxZ) / 2;
    const widthX = maxX - minX;
    const widthZ = maxZ - minZ;
    // Minimum span 350 ensures a stationary line of cars (120 m apart × 3 = 360 m wide) is fully visible
    const span = Math.max(350, widthX, widthZ);
    const height = Math.max(80, span * 0.55);
    const back = Math.max(80, span * 0.55);
    this.scene.camera.position.set(centerX, height, minZ - back * 0.25);
    this.scene.camera.lookAt(centerX, 0, centerZ);
  }

  // ──────────────────────────────────────────────────────────
  //  Input + lifecycle
  // ──────────────────────────────────────────────────────────

  private onKey(e: KeyboardEvent): void {
    switch (e.code) {
      case "Enter":
        if (this.runner.phase === "idle" || this.runner.phase === "done") {
          this.runner.start();
        } else {
          this.runner.continueFromWait();
        }
        break;
      case "KeyN":
        this.runner.nextPhase();
        break;
      case "KeyR":
        this.clearCombat();
        this.runner.reset();
        this.resetVehicles();
        this.ui.show();
        this.ui.setVehicleNames(this.lanes.map((l) => l.blueprint.name));
        break;
      case "KeyP":
        this.paused = !this.paused;
        break;
      case "Escape":
        this.stop();
        break;
    }
  }

  private resetVehicles(): void {
    for (let i = 0; i < this.lanes.length; i++) {
      const lane = this.lanes[i];
      lane.controller.teleport(lane.spawnX, 0, lane.spawnZ, 0);
    }
  }

  private spawnVehicles(): void {
    const max = Math.min(this.blueprints.length, LANE_X.length);
    for (let i = 0; i < max; i++) {
      const bp = this.blueprints[i];
      const upgrade = this.upgrades[i] ?? CAR_PRESETS["stock"];
      const sx = LANE_X[i];
      const sz = START_Z;
      const controller = new VehicleController(NO_OBSTACLES, bp, {
        x: sx, y: 0, z: sz, rotY: 0,
      }, { arenaSize: TEST_ARENA_SIZE });
      controller.applyUpgrades(upgrade);
      const visual = buildVehicleVisual(bp);
      this.scene.scene.add(visual.group);

      const presetName = findPresetName(upgrade);
      const labelName = presetName !== "stock" ? `${bp.name} [${presetName}]` : bp.name;
      const { ctx, tex, sprite } = this.makeLabel(labelName);
      this.scene.scene.add(sprite);

      this.lanes.push({
        blueprint: bp, controller, visual,
        labelSprite: sprite, labelCtx: ctx, labelTex: tex,
        spawnX: sx, spawnZ: sz,
        reloadTimer: 0, shotsFired: 0, shotsHit: 0,
        labelName,
      });
    }
  }

  private makeLabel(name: string): { ctx: CanvasRenderingContext2D; tex: THREE.CanvasTexture; sprite: THREE.Sprite } {
    const canvas = document.createElement("canvas");
    canvas.width = 240; canvas.height = 64;
    const ctx = canvas.getContext("2d")!;
    const tex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(8, 2.2, 1);
    this.drawLabel(ctx, tex, name, "— m/s");
    return { ctx, tex, sprite };
  }

  private drawLabel(ctx: CanvasRenderingContext2D, tex: THREE.CanvasTexture, name: string, speed: string): void {
    ctx.clearRect(0, 0, 240, 64);
    ctx.fillStyle = "rgba(4,6,13,0.78)";
    ctx.fillRect(0, 0, 240, 64);
    ctx.font = "bold 16px monospace";
    ctx.fillStyle = "#22d3ee";
    ctx.textAlign = "center";
    ctx.fillText(name, 120, 22);
    ctx.font = "bold 22px monospace";
    ctx.fillStyle = "#fb923c";
    ctx.fillText(speed, 120, 52);
    tex.needsUpdate = true;
  }

  private updateLabel(lane: VehicleLane, speed: number): void {
    this.drawLabel(lane.labelCtx, lane.labelTex, lane.labelName, `${Math.abs(speed).toFixed(1)} m/s`);
  }

  // ──────────────────────────────────────────────────────────
  //  Open arena geometry
  // ──────────────────────────────────────────────────────────

  private buildArena(): void {
    const floorGeo = new THREE.PlaneGeometry(ARENA_WIDTH, ARENA_DEPTH);
    const floorMat = new THREE.MeshBasicMaterial({ color: 0x04060d });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.z = 0;
    this.scene.scene.add(floor);

    this.scene.scene.add(this.buildGrid(ARENA_WIDTH, ARENA_DEPTH, 25, 0x0a1f2e));
    this.scene.scene.add(this.buildArenaBorder());
    for (let i = 0; i < 4; i++) {
      this.scene.scene.add(this.buildBayCircle(LANE_X[i], START_Z, BAY_RADIUS));
      this.scene.scene.add(this.buildSpawnMarker(LANE_X[i], START_Z));
    }

    const hemi = new THREE.HemisphereLight(0x88ccff, 0x110022, 0.6);
    this.scene.scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 0.4);
    dir.position.set(40, 80, 30);
    this.scene.scene.add(dir);
  }

  private buildGrid(w: number, h: number, step: number, color: number): THREE.LineSegments {
    const pts: number[] = [];
    const hw = w / 2;
    const hh = h / 2;
    for (let x = -hw; x <= hw; x += step) {
      pts.push(x, 0.01, -hh, x, 0.01, hh);
    }
    for (let z = -hh; z <= hh; z += step) {
      pts.push(-hw, 0.01, z, hw, 0.01, z);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color }));
  }

  private buildArenaBorder(): THREE.LineSegments {
    const hw = ARENA_WIDTH / 2;
    const hh = ARENA_DEPTH / 2;
    const pts = [
      -hw, 0.05, -hh,  hw, 0.05, -hh,
       hw, 0.05, -hh,  hw, 0.05,  hh,
       hw, 0.05,  hh, -hw, 0.05,  hh,
      -hw, 0.05,  hh, -hw, 0.05, -hh,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0x22d3ee, opacity: 0.5, transparent: true }));
  }

  private buildBayCircle(cx: number, cz: number, radius: number): THREE.LineSegments {
    const pts: number[] = [];
    const segs = 64;
    for (let i = 0; i < segs; i++) {
      const a1 = (i / segs) * Math.PI * 2;
      const a2 = ((i + 1) / segs) * Math.PI * 2;
      pts.push(cx + Math.cos(a1) * radius, 0.03, cz + Math.sin(a1) * radius);
      pts.push(cx + Math.cos(a2) * radius, 0.03, cz + Math.sin(a2) * radius);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0x22d3ee, opacity: 0.25, transparent: true }));
  }

  private buildSpawnMarker(cx: number, cz: number): THREE.LineSegments {
    // Small cross at spawn point
    const pts = [
      cx - 2, 0.04, cz,  cx + 2, 0.04, cz,
      cx, 0.04, cz - 2,  cx, 0.04, cz + 2,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0xfb923c }));
  }
}

function findPresetName(u: CarUpgrade): string {
  for (const [k, v] of Object.entries(CAR_PRESETS)) {
    if ((Object.keys(u) as Array<keyof CarUpgrade>).every((key) => u[key] === v[key])) return k;
  }
  return "custom";
}
